import React, { useState, useRef } from 'react';
import {
  Plus, Trash2, Printer, CheckCircle, Clock, Send,
  ChevronDown, ChevronRight, DollarSign, Wallet,
} from 'lucide-react';
import { currencySymbol } from '../utils/finance';

const STATUSES = [
  { key: 'pending', label: 'Pending', icon: Clock, color: '#D4AF37' },
  { key: 'submitted', label: 'Submitted', icon: Send, color: '#4A6FA5' },
  { key: 'received', label: 'Received', icon: CheckCircle, color: '#7A8B8F' },
];

const CATEGORIES = [
  'Travel', 'Equipment', 'Supplies', 'F&B', 'Transport',
  'Marketing', 'Maintenance', 'Utilities', 'Professional Services', 'Other',
];

export default function ClaimsTracker({
  business,
  isOwner,
  onClaimsChange,
  onPettyCashChange,
}) {
  const [showForm, setShowForm] = useState(false);
  const [expandedClaim, setExpandedClaim] = useState(null);
  const [editingFloat, setEditingFloat] = useState(false);
  const printRef = useRef();

  const claims = business.claims || [];
  const pettyCash = business.pettyCash || { float: 0, currency: 'SGD' };
  const pcSym = currencySymbol(pettyCash.currency);

  // Calculate petty cash balance
  const totalClaimed = claims
    .filter((c) => c.currency === pettyCash.currency && c.status !== 'received')
    .reduce((sum, c) => sum + c.amount, 0);
  const totalReceived = claims
    .filter((c) => c.currency === pettyCash.currency && c.status === 'received')
    .reduce((sum, c) => sum + c.amount, 0);
  const balance = pettyCash.float - totalReceived;

  const [form, setForm] = useState({
    description: '',
    amount: '',
    currency: pettyCash.currency,
    category: 'Other',
    fromAccount: '',
    date: new Date().toISOString().slice(0, 10),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) return;

    const newClaim = {
      id: `claim-${Date.now()}`,
      ...form,
      amount: parseFloat(form.amount),
      status: 'pending',
      receiptUrl: null,
      createdAt: new Date().toISOString(),
    };

    onClaimsChange([...claims, newClaim]);
    setForm({
      description: '',
      amount: '',
      currency: pettyCash.currency,
      category: 'Other',
      fromAccount: '',
      date: new Date().toISOString().slice(0, 10),
    });
    setShowForm(false);
  };

  const updateStatus = (claimId, status) => {
    onClaimsChange(
      claims.map((c) => (c.id === claimId ? { ...c, status } : c))
    );
  };

  const deleteClaim = (claimId) => {
    onClaimsChange(claims.filter((c) => c.id !== claimId));
  };

  const handleFloatChange = (value) => {
    onPettyCashChange({ ...pettyCash, float: parseFloat(value) || 0 });
    setEditingFloat(false);
  };

  const printClaimForm = (claim) => {
    const win = window.open('', '_blank', 'width=800,height=600');
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <meta name="format-detection" content="telephone=no, date=no, address=no, email=no">
        <meta name="color-scheme" content="light only">
        <title>Claim Form - ${business.name}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          body {
            font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            padding: 40px;
            color: #1a1a1a;
            max-width: 700px;
            margin: 0 auto;
          }
          h1 { font-size: 24px; margin-bottom: 4px; }
          .subtitle { color: #6B7280; margin-bottom: 32px; font-size: 14px; }
          .claim-details { border: 1px solid #E5E7EB; border-radius: 8px; padding: 24px; }
          .row { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding: 10px 0; border-bottom: 1px solid #F0F0F0; }
          .row:last-child { border-bottom: none; }
          .label { color: #6B7280; font-size: 14px; flex-shrink: 0; }
          .value { font-weight: 600; text-align: right; word-break: break-word; }
          .amount { font-size: 20px; color: #1a56db; font-family: 'DM Mono', monospace; }
          .footer { margin-top: 48px; display: flex; justify-content: space-between; gap: 24px; }
          .sig-line { border-top: 1px solid #3A3A3A; width: 200px; padding-top: 8px; text-align: center; color: #6B7280; font-size: 13px; }

          /* ── Tablet / mobile (≤768px) ── */
          @media (max-width: 768px) {
            body { padding: 20px; max-width: 100%; }
            h1 { font-size: 20px; }
            .subtitle { font-size: 13px; margin-bottom: 24px; }
            .claim-details { padding: 16px; }
            .row { padding: 8px 0; gap: 12px; }
            .label { font-size: 13px; }
            .value { font-size: 13px; }
            .amount { font-size: 18px; }
            .footer { flex-direction: column; gap: 32px; margin-top: 32px; }
            .sig-line { width: 100%; }
          }

          /* ── Small phone (≤420px) ── */
          @media (max-width: 420px) {
            body { padding: 14px; }
            .claim-details { padding: 14px; }
            .row { flex-direction: column; gap: 4px; padding: 10px 0; }
            .value { text-align: left; }
            .amount { font-size: 17px; }
          }

          @media print {
            body { padding: 20px; }
            .row { flex-direction: row; }
            .footer { flex-direction: row; }
          }
        </style>
      </head>
      <body>
        <h1>Expense Claim Form</h1>
        <p class="subtitle">${business.name} - ${business.type}</p>
        <div class="claim-details">
          <div class="row">
            <span class="label">Claim ID</span>
            <span class="value">${claim.id}</span>
          </div>
          <div class="row">
            <span class="label">Date</span>
            <span class="value">${claim.date}</span>
          </div>
          <div class="row">
            <span class="label">Description</span>
            <span class="value">${claim.description}</span>
          </div>
          <div class="row">
            <span class="label">Category</span>
            <span class="value">${claim.category}</span>
          </div>
          <div class="row">
            <span class="label">From Account</span>
            <span class="value">${claim.fromAccount || 'N/A'}</span>
          </div>
          <div class="row">
            <span class="label">Amount</span>
            <span class="value amount">${currencySymbol(claim.currency)}${claim.amount.toLocaleString()}</span>
          </div>
          <div class="row">
            <span class="label">Status</span>
            <span class="value">${claim.status.toUpperCase()}</span>
          </div>
        </div>
        <div class="footer">
          <div class="sig-line">Claimant Signature</div>
          <div class="sig-line">Approved By</div>
        </div>
      </body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  const pendingClaims = claims.filter((c) => c.status === 'pending');
  const submittedClaims = claims.filter((c) => c.status === 'submitted');
  const receivedClaims = claims.filter((c) => c.status === 'received');

  return (
    <div className="claims-tracker">
      {/* Petty Cash Summary */}
      <div className="petty-cash-bar">
        <div className="petty-cash-card">
          <Wallet size={20} />
          <div>
            <span className="pc-label">Petty Cash Float</span>
            {editingFloat && isOwner ? (
              <input
                type="number"
                className="pc-float-input"
                defaultValue={pettyCash.float}
                onBlur={(e) => handleFloatChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleFloatChange(e.target.value)}
                autoFocus
              />
            ) : (
              <span
                className="pc-value"
                onClick={() => isOwner && setEditingFloat(true)}
                title={isOwner ? 'Click to edit' : ''}
              >
                {pcSym}{pettyCash.float.toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div className="petty-cash-card">
          <DollarSign size={20} />
          <div>
            <span className="pc-label">Pending Claims</span>
            <span className="pc-value warning">{pcSym}{totalClaimed.toLocaleString()}</span>
          </div>
        </div>
        <div className="petty-cash-card">
          <CheckCircle size={20} />
          <div>
            <span className="pc-label">Total Received</span>
            <span className="pc-value positive">{pcSym}{totalReceived.toLocaleString()}</span>
          </div>
        </div>
        <div className="petty-cash-card highlight">
          <div>
            <span className="pc-label">Balance</span>
            <span className={`pc-value ${balance >= 0 ? 'positive' : 'negative'}`}>
              {pcSym}{balance.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Add claim button */}
      {isOwner && (
        <div className="claims-actions">
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
            <Plus size={18} /> New Claim
          </button>
        </div>
      )}

      {/* New claim form */}
      {showForm && (
        <form className="claim-form" onSubmit={handleSubmit}>
          <div className="claim-form-grid">
            <div className="form-field">
              <label>Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What is this claim for?"
                required
              />
            </div>
            <div className="form-field">
              <label>Amount</label>
              <div className="amount-input-group">
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                >
                  {(business.currencies || ['SGD']).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>
            <div className="form-field">
              <label>Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>From Account / Business</label>
              <input
                type="text"
                value={form.fromAccount}
                onChange={(e) => setForm({ ...form, fromAccount: e.target.value })}
                placeholder="e.g. KNG Petty Cash, NK Operating"
              />
            </div>
            <div className="form-field">
              <label>Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">Add Claim</button>
            <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* Claims by status */}
      {[
        { label: 'Pending', items: pendingClaims, status: 'pending' },
        { label: 'Submitted', items: submittedClaims, status: 'submitted' },
        { label: 'Received', items: receivedClaims, status: 'received' },
      ].map(({ label, items, status }) => {
        if (items.length === 0) return null;
        const statusInfo = STATUSES.find((s) => s.key === status);
        return (
          <div key={status} className="claims-section">
            <h3 className="claims-section-title" style={{ color: statusInfo.color }}>
              <statusInfo.icon size={16} /> {label} ({items.length})
            </h3>
            <div className="claims-list">
              {items.map((claim) => (
                <ClaimRow
                  key={claim.id}
                  claim={claim}
                  isOwner={isOwner}
                  isExpanded={expandedClaim === claim.id}
                  onToggle={() => setExpandedClaim(expandedClaim === claim.id ? null : claim.id)}
                  onStatusChange={(s) => updateStatus(claim.id, s)}
                  onDelete={() => deleteClaim(claim.id)}
                  onPrint={() => printClaimForm(claim)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {claims.length === 0 && (
        <div className="nk-empty-state">
          <div className="nk-empty-icon"><DollarSign size={36} /></div>
          <div className="nk-empty-title">No claims yet</div>
          <div className="nk-empty-body">Add expense claims to reconcile against business accounts.</div>
        </div>
      )}
    </div>
  );
}

function ClaimRow({ claim, isOwner, isExpanded, onToggle, onStatusChange, onDelete, onPrint }) {
  const sym = currencySymbol(claim.currency);

  return (
    <div className="claim-row">
      <button className="claim-row-header" onClick={onToggle}>
        <div className="claim-row-left">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="claim-date mono">{claim.date}</span>
          <span className="claim-desc">{claim.description}</span>
        </div>
        <div className="claim-row-right">
          <span className="claim-category-badge">{claim.category}</span>
          <span className="claim-amount mono">{sym}{claim.amount.toLocaleString()}</span>
        </div>
      </button>

      {isExpanded && (
        <div className="claim-details">
          <div className="claim-detail-row">
            <span className="detail-label">From</span>
            <span>{claim.fromAccount || 'Not specified'}</span>
          </div>
          <div className="claim-detail-row">
            <span className="detail-label">Status</span>
            {isOwner ? (
              <div className="status-toggle">
                {STATUSES.map((s) => (
                  <button
                    key={s.key}
                    className={`status-btn ${claim.status === s.key ? 'active' : ''}`}
                    style={{ '--status-color': s.color }}
                    onClick={() => onStatusChange(s.key)}
                  >
                    <s.icon size={14} /> {s.label}
                  </button>
                ))}
              </div>
            ) : (
              <span style={{ color: STATUSES.find((s) => s.key === claim.status)?.color }}>
                {claim.status}
              </span>
            )}
          </div>
          {isOwner && (
            <div className="claim-detail-actions">
              <button className="btn btn-ghost btn-sm" onClick={onPrint}>
                <Printer size={14} /> Print Claim Form
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onDelete} style={{ color: '#f87171' }}>
                <Trash2 size={14} /> Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
